// src/pages/outsourcing_management/requisitions/requisitionForm.ts
//
// EE-207 Phase 3 (#9-#12) - shared, non-component form logic for the
// create/edit Draft form: local form-item shape, empty-item factories,
// client-side validation (mirrors backend/controllers/purchasing/
// RequisitionController.py _build_item/_apply_header_and_items
// exactly), the material-total preview formula, and the payload builder that
// turns form state back into the camelCase Create/UpdateRequisitionType
// the network layer expects.
import { InquiryTypeEnum } from "types/InquiryTypes";
import {
  CreateRequisitionItemType,
  CreateRequisitionType,
  RequisitionItemPrefillType,
  RequisitionItemType,
  RequisitionOptionsType,
} from "types/RequisitionTypes";

export const REQ_TYPE_RAW_MATERIAL = "RAW_MATERIAL";
// Raw material bought already cut - waterjet, laser - and machined in-house
// afterwards. A material line in every respect: same fields, same payload,
// same Inquiry. The one difference is what the supplier is sent - see
// sendsPartFiles.
export const REQ_TYPE_PROCESSED_RAW_MATERIAL = "PROCESSED_RAW_MATERIAL";
export const REQ_TYPE_TOOL = "TOOL";

// The outsourcing types. Codes match InquiryTypeEnum exactly - each one
// exists so its Inquiry can be raised from a requisition rather than created
// directly. Mirrors REQ_TYPE_* in RequisitionConfigController.py.
export const REQ_TYPE_CNC = "CNC";
export const REQ_TYPE_SHEET = "SHEET";
export const REQ_TYPE_FINISH = "FINISH";
export const REQ_TYPE_CUSTOM = "CUSTOM";

// Purposes that name a part. Mirrors PURPOSE_* in
// RequisitionConfigController.py; both carry
// requires_sales_order_and_part, and which of the two references the
// requisition then holds is the order-XOR-quote rule the backend enforces.
export const PURPOSE_SALES_ORDER = "SALES_ORDER";
export const PURPOSE_SALES_QUOTE = "SALES_QUOTE";

// Types whose lines point at a part that already exists rather than
// describing one in words - mirrors PART_BASED_TYPES on the backend, which
// re-validates and remains the authority.
const PART_BASED_TYPES = [
  REQ_TYPE_CNC,
  REQ_TYPE_SHEET,
  REQ_TYPE_FINISH,
  REQ_TYPE_CUSTOM,
];

export const isPartBasedType = (
  typeCode: string | null | undefined
): boolean => !!typeCode && PART_BASED_TYPES.includes(typeCode);

// Types whose lines describe stock - material type, profile, dimensions -
// rather than pointing at a part. Mirrors MATERIAL_TYPES on the backend.
const MATERIAL_TYPES = [
  REQ_TYPE_RAW_MATERIAL,
  REQ_TYPE_PROCESSED_RAW_MATERIAL,
];

export const isMaterialType = (
  typeCode: string | null | undefined
): boolean => !!typeCode && MATERIAL_TYPES.includes(typeCode);

// Types whose Inquiry email carries each part's model and drawing next to
// the Inquiry PDF. The rest - raw material, finishing, tools - send the
// Inquiry PDF alone, so what the requester types on the line is all the
// supplier gets. Purchasing's rule; the backend senders in EmailService.py
// are the authority, this only tells the requester what to expect.
const TYPES_SENDING_PART_FILES = [
  REQ_TYPE_CNC,
  REQ_TYPE_SHEET,
  REQ_TYPE_CUSTOM,
  REQ_TYPE_PROCESSED_RAW_MATERIAL,
];

export const sendsPartFiles = (
  typeCode: string | null | undefined
): boolean => !!typeCode && TYPES_SENDING_PART_FILES.includes(typeCode);

// Which service a requisition of each type is asking for, as the Inquiry type
// code that a partner's register entry (PARTNERS.template_type) is written in.
// Mirrors _INQUIRY_TYPE_BY_REQ_TYPE in
// backend/controllers/purchasing/RequisitionEnquiryController.py, which is the
// authority - it is the map the Inquiry raised from a requisition is actually
// built with. Every code is the same word on both sides except raw material,
// whose Inquiry type is the older MATERIAL.
const INQUIRY_TYPE_BY_REQ_TYPE: Record<string, InquiryTypeEnum> = {
  [REQ_TYPE_RAW_MATERIAL]: InquiryTypeEnum.MATERIAL,
  [REQ_TYPE_PROCESSED_RAW_MATERIAL]: InquiryTypeEnum.PROCESSED_RAW_MATERIAL,
  [REQ_TYPE_TOOL]: InquiryTypeEnum.TOOL,
  [REQ_TYPE_CNC]: InquiryTypeEnum.CNC,
  [REQ_TYPE_SHEET]: InquiryTypeEnum.SHEET,
  [REQ_TYPE_FINISH]: InquiryTypeEnum.FINISH,
  [REQ_TYPE_CUSTOM]: InquiryTypeEnum.CUSTOM,
};

// Undefined for a type this map has not caught up with, which the supplier
// picker reads as "no service to filter on" and so offers everybody.
export const inquiryTypeForRequisitionType = (
  typeCode: string | null | undefined
): InquiryTypeEnum | undefined =>
  typeCode ? INQUIRY_TYPE_BY_REQ_TYPE[typeCode] : undefined;
export const SHAPE_ROUND = "ROUND";
export const SHAPE_FLAT = "FLAT";

// ---------------------------------------------------------------------------
// Local form item shape - superset of CreateRequisitionItemType with
// a stable `_key` for React list rendering (array index alone breaks on
// remove/reorder).
// ---------------------------------------------------------------------------

export type RequisitionFormItem = {
  _key: string;
  id?: number;

  // Raw material fields
  certificateRequired?: boolean; // undefined = not chosen yet (tri-state Y/N)
  materialTypeCode?: string;
  materialTypeOtherDescription?: string;
  profileTypeCode?: string;
  diameterMm?: number;
  widthMm?: number;
  heightMm?: number;
  partLengthMm?: number;
  numberOfParts?: number;
  cuttingAllowanceMm?: number;

  // Tool fields
  toolName?: string;
  toolCategoryCode?: string;
  quantity?: number;

  // Part-based fields (CNC / Sheet / Finishing / Custom) - the line points at
  // a part instead of describing one. A material line picked from a part
  // carries them too, which is how a processed raw material line gets the
  // part's model and drawing onto the Inquiry (sendsPartFiles).
  productId?: number;
  productName?: string;
  orderItemId?: number;
  quoteItemId?: number;

  description?: string;
};

let keyCounter = 0;
export const makeItemKey = () => `pr-item-${Date.now()}-${keyCounter++}`;

export const createEmptyItem = (
  cuttingAllowanceMm?: number
): RequisitionFormItem => ({
  _key: makeItemKey(),
  cuttingAllowanceMm,
});

// Hydrate an item returned from GET (snake_case) into the local form shape,
// for the future edit-Draft path.
export const itemFromApi = (
  item: RequisitionItemType
): RequisitionFormItem => ({
  _key: makeItemKey(),
  id: item.id,
  certificateRequired: item.certificate_required ?? undefined,
  materialTypeCode: item.material_type_code ?? undefined,
  materialTypeOtherDescription: item.material_type_other_description ?? undefined,
  profileTypeCode: item.profile_type_code ?? undefined,
  diameterMm: item.diameter_mm ?? undefined,
  widthMm: item.width_mm ?? undefined,
  heightMm: item.height_mm ?? undefined,
  partLengthMm: item.part_length_mm ?? undefined,
  numberOfParts: item.number_of_parts ?? undefined,
  cuttingAllowanceMm: item.cutting_allowance_mm ?? undefined,
  toolName: item.tool_name ?? undefined,
  toolCategoryCode: item.tool_category_code ?? undefined,
  quantity: item.quantity ?? undefined,
  productId: item.product_id ?? undefined,
  productName: item.product_name ?? undefined,
  orderItemId: item.order_item_id ?? undefined,
  quoteItemId: item.quote_item_id ?? undefined,
  description: item.description ?? undefined,
});

// Build a raw-material line from the SO part's product
// (GET /requisitions/item-prefill - see RequisitionPrefillController).
// Only fields the backend could actually derive are set; everything else is
// left undefined so the normal "required" validation still catches it. The
// result is an ordinary editable line - nothing here is locked. The ref tags
// the line with the part it came from, so the requisition remembers which
// part each material line serves and re-selecting parts can find the line.
export const itemFromPrefill = (
  prefill: RequisitionItemPrefillType,
  cuttingAllowanceMm?: number,
  ref?: { orderItemId?: number; quoteItemId?: number }
): RequisitionFormItem => ({
  _key: makeItemKey(),
  cuttingAllowanceMm,
  orderItemId: ref?.orderItemId,
  quoteItemId: ref?.quoteItemId,
  certificateRequired: prefill.certificate_required ?? undefined,
  materialTypeCode: prefill.material_type_code ?? undefined,
  materialTypeOtherDescription: prefill.material_type_other_description ?? undefined,
  profileTypeCode: prefill.profile_type_code ?? undefined,
  diameterMm: prefill.diameter_mm ?? undefined,
  widthMm: prefill.width_mm ?? undefined,
  heightMm: prefill.height_mm ?? undefined,
  partLengthMm: prefill.part_length_mm ?? undefined,
  numberOfParts: prefill.number_of_parts ?? undefined,
});

// Build a part-based line (CNC / Sheet / Finishing / Custom) from a part
// picked on the header - one line per picked part. These lines describe that
// exact part, so everything here is already known - re-keying the quantity
// off the order is busywork and a chance to get it wrong. The line stays
// fully editable: a requisition may deliberately ask for fewer than were
// ordered.
export const itemFromPart = (part: {
  orderItemId?: number;
  quoteItemId?: number;
  productId?: number;
  name?: string;
  quantity?: number;
}): RequisitionFormItem => ({
  _key: makeItemKey(),
  orderItemId: part.orderItemId,
  quoteItemId: part.quoteItemId,
  productId: part.productId,
  productName: part.name,
  // 0 would read as "none required" and pass through as a falsy quantity the
  // validator then rejects with no explanation, so it is left unset.
  quantity: part.quantity && part.quantity > 0 ? part.quantity : undefined,
});

// Did the backend find anything worth prefilling? An OTHER product (no
// details row) comes back as an all-null row - adding a blank line "from the
// part" would be a lie, so callers skip it.
export const prefillHasValues = (prefill: RequisitionItemPrefillType) =>
  prefill.material_type_code !== null ||
  prefill.certificate_required !== null ||
  prefill.part_length_mm !== null ||
  prefill.number_of_parts !== null;

// ---------------------------------------------------------------------------
// Header (general info) form values - camelCase, dates kept as "" (not
// undefined) while editing so MUI date TextFields stay controlled.
// ---------------------------------------------------------------------------

export type RequisitionFormHeaderValues = {
  typeCode: string;
  purposeCode: string;
  salesOrderId?: number;
  // Every part picked on the header, in pick order - one item line each.
  // The backend header keeps only the first; the lines carry the rest.
  orderItemIds: number[];
  // A requisition is raised from an order or from a quote, never both -
  // mirrors the exclusivity RequisitionController enforces.
  quoteId?: number;
  quoteItemIds: number[];
  supplierId?: number;
  requiredByDate: string;
  estimatedDeliveryDate: string;
  comments: string;
};

export const emptyHeaderValues = (): RequisitionFormHeaderValues => ({
  typeCode: "",
  purposeCode: "",
  salesOrderId: undefined,
  orderItemIds: [],
  quoteId: undefined,
  quoteItemIds: [],
  supplierId: undefined,
  requiredByDate: "",
  estimatedDeliveryDate: "",
  comments: "",
});

// ---------------------------------------------------------------------------
// Material-total preview - mirrors
// RequisitionController.calculate_material verbatim:
//   Total Cutting Length = Part Length x Number of Parts
//   Total Required Material = Total Cutting Length + (Cutting Allowance x Number of Parts)
// The backend computes ROUND and FLAT identically today (called out there as
// an open product question, not a frontend omission) - this preview mirrors
// that exactly and must NOT invent a flat-specific formula.
// ---------------------------------------------------------------------------

export const calculateMaterialPreview = (
  partLengthMm: number | undefined,
  numberOfParts: number | undefined,
  cuttingAllowanceMm: number | undefined
) => {
  const partLength = partLengthMm ?? 0;
  const parts = numberOfParts ?? 0;
  const allowance = cuttingAllowanceMm ?? 0;
  const totalCuttingLengthMm = partLength * parts;
  const totalAllowanceMm = allowance * parts;
  const totalRequiredMaterialMm = totalCuttingLengthMm + totalAllowanceMm;
  return { totalCuttingLengthMm, totalAllowanceMm, totalRequiredMaterialMm };
};

// ---------------------------------------------------------------------------
// Validation - mirrors RequisitionController._apply_header_and_items
// / _build_item exactly (see backend/controllers/purchasing/
// RequisitionController.py), so a client-valid submission is never
// rejected by the server for a rule this form didn't already check.
// ---------------------------------------------------------------------------

export type RequisitionHeaderErrors = Partial<
  Record<"typeCode" | "purposeCode" | "salesOrderId" | "orderItemId", string>
>;

export const validateHeader = (
  values: RequisitionFormHeaderValues,
  options: RequisitionOptionsType | undefined
): RequisitionHeaderErrors => {
  const errors: RequisitionHeaderErrors = {};
  if (!values.typeCode) errors.typeCode = "Type is required";
  if (!values.purposeCode) errors.purposeCode = "Purpose is required";

  const purpose = options?.purposes.find((p) => p.code === values.purposeCode);
  if (purpose?.requiresSalesOrderAndPart) {
    // Either pair satisfies it: a requisition raised from a Quote has no
    // sales order, and demanding one would make the Quote entry point
    // unusable. Exactly one of the two - the backend refuses both.
    // `.length > 0`, not `!!array`: an empty array is truthy.
    const hasQuote = !!values.quoteId || values.quoteItemIds.length > 0;
    if (!hasQuote) {
      if (!values.salesOrderId)
        errors.salesOrderId = "Sales Order is required for this purpose";
      if (values.orderItemIds.length === 0)
        errors.orderItemId = "Select at least one part for this purpose";
    } else {
      if (!values.quoteId) errors.salesOrderId = "Quote is required for this purpose";
      if (values.quoteItemIds.length === 0)
        errors.orderItemId = "Select at least one part for this purpose";
    }
  }
  return errors;
};

export type RequisitionItemErrors = Partial<
  Record<
    | "materialTypeCode"
    | "materialTypeOtherDescription"
    | "certificateRequired"
    | "profileTypeCode"
    | "diameterMm"
    | "widthMm"
    | "heightMm"
    | "partLengthMm"
    | "numberOfParts"
    | "toolName"
    | "quantity"
    | "orderItemId",
    string
  >
>;

export const validateItem = (
  typeCode: string,
  item: RequisitionFormItem,
  options: RequisitionOptionsType | undefined
): RequisitionItemErrors => {
  const errors: RequisitionItemErrors = {};

  if (isMaterialType(typeCode)) {
    if (!item.materialTypeCode) {
      errors.materialTypeCode = "Material type is required";
    }
    const materialType = options?.materialTypes.find(
      (m) => m.code === item.materialTypeCode
    );
    if (materialType?.requiresDescription && !item.materialTypeOtherDescription?.trim()) {
      errors.materialTypeOtherDescription =
        "Description is required when material type is Other";
    }

    if (item.certificateRequired === undefined) {
      errors.certificateRequired = "Certificate required (Y/N) must be set";
    }

    if (!item.profileTypeCode) {
      errors.profileTypeCode = "Profile is required";
    }
    const profile = options?.profileTypes.find((p) => p.code === item.profileTypeCode);
    if (profile?.shapeClass === SHAPE_ROUND) {
      if (!item.diameterMm || item.diameterMm <= 0) {
        errors.diameterMm = "Diameter must be a positive number";
      }
    } else if (profile?.shapeClass === SHAPE_FLAT) {
      if (!item.widthMm || item.widthMm <= 0) {
        errors.widthMm = "Width must be a positive number";
      }
      if (!item.heightMm || item.heightMm <= 0) {
        errors.heightMm = "Height must be a positive number";
      }
    }

    if (!item.partLengthMm || item.partLengthMm <= 0) {
      errors.partLengthMm = "Part length must be a positive number";
    }
    if (!item.numberOfParts || item.numberOfParts <= 0) {
      errors.numberOfParts = "Number of parts must be a positive integer";
    }
  } else if (typeCode === REQ_TYPE_TOOL) {
    if (!item.toolName?.trim()) {
      errors.toolName = "Tool name is required";
    }
    if (!item.quantity || item.quantity <= 0) {
      errors.quantity = "Quantity must be a positive integer";
    }
  } else if (isPartBasedType(typeCode)) {
    // The part reference is not validated per line: the lines are built
    // from the parts picked on the header (one each), and the header check
    // already demands at least one. A line with no ref of its own falls back
    // to the header's first part (see buildItemPayload).
    if (!item.quantity || item.quantity <= 0) {
      errors.quantity = "Quantity must be a positive integer";
    }
  }

  return errors;
};

export const hasAnyItemError = (errors: RequisitionItemErrors) =>
  Object.keys(errors).length > 0;

// ---------------------------------------------------------------------------
// Payload builder - form state -> camelCase Create/UpdateRequisitionType
// ---------------------------------------------------------------------------

// Only the dimensions that belong to the selected profile's shapeClass are
// sent (ROUND -> diameter, FLAT -> width/height). The backend nulls the
// irrelevant ones anyway, but sending a stale diameter along with a FLAT
// profile is a lie about what the user entered, and would resurface if the
// server ever stopped normalising.
export const buildItemPayload = (
  typeCode: string,
  item: RequisitionFormItem,
  options?: RequisitionOptionsType,
  header?: RequisitionFormHeaderValues
): CreateRequisitionItemType => {
  if (isMaterialType(typeCode)) {
    const shapeClass = options?.profileTypes.find(
      (p) => p.code === item.profileTypeCode
    )?.shapeClass;
    const isRound = shapeClass === SHAPE_ROUND;
    const isFlat = shapeClass === SHAPE_FLAT;
    return {
      id: item.id,
      certificateRequired: item.certificateRequired,
      materialTypeCode: item.materialTypeCode,
      materialTypeOtherDescription: item.materialTypeOtherDescription,
      profileTypeCode: item.profileTypeCode,
      diameterMm: isRound ? item.diameterMm : undefined,
      widthMm: isFlat ? item.widthMm : undefined,
      heightMm: isFlat ? item.heightMm : undefined,
      partLengthMm: item.partLengthMm,
      numberOfParts: item.numberOfParts,
      cuttingAllowanceMm: item.cuttingAllowanceMm,
      // The part this material line was prefilled from, if any. A manual
      // Add Line row has neither and describes stock for the order as a
      // whole.
      orderItemId: item.orderItemId,
      quoteItemId: item.orderItemId ? undefined : item.quoteItemId,
      description: item.description,
    };
  }
  if (isPartBasedType(typeCode)) {
    // The line is the authority: with several parts picked on the header
    // each line points at its own part, and the header only carries the
    // first of them. The header is the fallback for a line with no ref of
    // its own (one loaded from before lines carried refs).
    const orderItemId = item.orderItemId ?? header?.orderItemIds[0];
    const quoteItemId = item.quoteItemId ?? header?.quoteItemIds[0];
    return {
      id: item.id,
      productId: item.productId,
      // Exactly one reference reaches the backend, which refuses both. If
      // both are somehow set the order side wins rather than nulling the
      // pair, which would save a line with no part at all.
      orderItemId,
      quoteItemId: orderItemId ? undefined : quoteItemId,
      quantity: item.quantity,
      description: item.description,
    };
  }
  return {
    id: item.id,
    toolName: item.toolName,
    toolCategoryCode: item.toolCategoryCode,
    quantity: item.quantity,
    description: item.description,
  };
};

export const buildRequisitionPayload = (
  header: RequisitionFormHeaderValues,
  items: RequisitionFormItem[],
  options?: RequisitionOptionsType
): CreateRequisitionType => ({
  typeCode: header.typeCode,
  purposeCode: header.purposeCode,
  salesOrderId: header.salesOrderId,
  // The header field holds one part; the first pick fills it and the lines
  // carry every pick.
  orderItemId: header.orderItemIds[0],
  quoteId: header.quoteId,
  quoteItemId: header.quoteItemIds[0],
  supplierId: header.supplierId,
  requiredByDate: header.requiredByDate || undefined,
  estimatedDeliveryDate: header.estimatedDeliveryDate || undefined,
  comments: header.comments || undefined,
  items: items.map((item) =>
    buildItemPayload(header.typeCode, item, options, header)
  ),
});

// ---------------------------------------------------------------------------
// Read-only summary of the parts a requisition covers, for the table and the
// detail dialog: "Bracket" / "Bracket, Housing" / "Bracket, Housing +2".
// Falls back to the header part when no line names one (older rows).
// ---------------------------------------------------------------------------

const PART_SUMMARY_MAX_NAMES = 2;

export const partSummary = (
  items: Pick<RequisitionItemType, "product_name">[] | undefined,
  fallbackName: string | null | undefined
): string | null => {
  const names = Array.from(
    new Set(
      (items ?? [])
        .map((item) => item.product_name)
        .filter((name): name is string => !!name)
    )
  );
  if (names.length === 0) return fallbackName ?? null;
  const shown = names.slice(0, PART_SUMMARY_MAX_NAMES).join(", ");
  const rest = names.length - PART_SUMMARY_MAX_NAMES;
  return rest > 0 ? `${shown} +${rest}` : shown;
};
